<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'DetailDialog',
})
</script>
<template>
	<el-drawer v-model="visible" :title="props.title || defaultTitle" :size="width" direction="rtl" @close="handleClose">
		<div class="detail-content">
			<el-descriptions :column="column" border>
				<el-descriptions-item v-for="field in fields" :key="field.prop" :label="field.label" :span="field.span || 1">
					<!-- 字典类型 -->
					<ma-dict-label v-if="field.type === 'dict'" :dict-type="field.dict" :dict-value="data[field.prop]" />
					<!-- 文件类型 -->
					<div v-else-if="field.type === 'file'" class="file-list">
						<template v-if="data[field.prop] && data[field.prop].length > 0">
							<div class="fileBox">
								<div class="fileGrid">
									<div v-for="item in data[field.prop]" :key="item.url" class="fileItem">
										<div class="fileContent" @click="handlePreview(item.url)">
											<div class="fileIcon">
												<img :src="getFileTypeSvg(item.url)" :alt="getFileType(item.url)" class="iconImage" />
											</div>
											<div class="fileInfo">
												<div class="fileName">{{ item.name || item.originalName || t('componentsModule.common.detailDialog.unnamedFile') }}</div>
												<div class="fileSize">{{ formatFileSize(item.size || 0) }}</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</template>
						<span v-else class="empty-text">{{ t('componentsModule.common.detailDialog.noFiles') }}</span>
					</div>
					<!-- 日期时间类型 -->
					<span v-else-if="field.type === 'datetime'">
						{{ formatDateTime(data[field.prop]) }}
					</span>
					<!-- 富文本类型 -->
					<ma-editor
						v-else-if="field.type === 'editor'"
						:model-value="data[field.prop]"
						:disabled="true"
						:style="'height: auto; min-height: 100px'"
					></ma-editor>

					<!-- 表格类型 -->
					<div v-else-if="field.type === 'table'" class="table-data">
						<template v-if="data[field.prop] && Array.isArray(data[field.prop]) && data[field.prop].length > 0">
							<el-table :data="data[field.prop]" border size="small" style="width: 100%">
								<el-table-column v-for="col in field.columns" :key="col.prop" :prop="col.prop" :label="col.label" />
							</el-table>
						</template>
						<span v-else class="empty-text">{{ t('common.tableCom.noData') }}</span>
					</div>

					<!-- 普通文本 -->
					<span v-else>
						{{ data[field.prop] || '-' }}
					</span>
				</el-descriptions-item>
			</el-descriptions>
		</div>
		<template #footer>
			<el-button @click="handleClose">{{ t('componentsModule.common.detailDialog.close') }}</el-button>
		</template>
	</el-drawer>
</template>

<script setup lang="ts" name="DetailDialog">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
const { t } = useI18n()
import { getFileTypeSvg, getFileType, formatFileSize } from '@/utils/fileTypeIcon'
import { handlePreview } from '@/utils/tool'

interface Field {
	label: string
	prop: string
	type?: 'dict' | 'file' | 'datetime' | 'text' | 'table'
	dict?: string
	span?: number
	columns?: Array<{
		prop: string
		label: string
		width?: string
	}>
}

interface Props {
	title?: string
	width?: string | number
	column?: number
	fields: Field[]
	data: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
	title: '',
	width: '60%',
	column: 2,
	fields: () => [],
	data: () => ({})
})

const visible = defineModel<boolean>()

const emit = defineEmits(['close'])

// 计算属性：默认标题
const defaultTitle = computed(() => {
	if (!props.title) {
		return t('componentsModule.componentsModule.common.detailDialog.detail')
	}
	return ''
})

const handleClose = () => {
	visible.value = false
	emit('close')
}

const formatDateTime = (dateTime: string) => {
	if (!dateTime) {
		return '-'
	}
	return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss')
}
</script>

<style lang="scss" scoped>
.detail-content {
	overflow-y: auto;
}

.file-list {
	.empty-text {
		color: #999;
		font-size: 14px;
		font-style: italic;
	}

	.fileBox {
		margin-top: 10px;
	}

	.fileGrid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
		gap: 10px;
	}

	.fileItem {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px;
		border: 1px solid #ebeef5;
		border-radius: 4px;
		background-color: #f8f8f8;
	}

	.fileContent {
		display: flex;
		align-items: center;
		cursor: pointer;
		flex: 1;
		overflow: hidden;
	}

	.fileIcon {
		width: 40px;
		height: 40px;
		margin-right: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.iconImage {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	.fileInfo {
		flex: 1;
		overflow: hidden;
	}

	.fileName {
		font-size: 14px;
		font-weight: 500;
		color: #303133;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.fileSize {
		font-size: 12px;
		color: #909399;
		margin-top: 2px;
	}
}

.table-data {
	margin-top: 8px;

	.el-table {
		border-radius: 4px;
		overflow: hidden;

		:deep(.el-table__header-wrapper) {
			th {
				background-color: #f5f7fa;
				color: #606266;
				font-weight: 600;
				font-size: 13px;
			}
		}

		:deep(.el-table__body-wrapper) {
			td {
				font-size: 13px;
				color: #606266;
			}
		}

		:deep(.el-table__border) {
			border-color: #ebeef5;
		}

		:deep(.el-table td, .el-table th) {
			border-color: #ebeef5;
		}
	}

	.empty-text {
		color: #999;
		font-size: 14px;
		font-style: italic;
		display: block;
		text-align: center;
		padding: 20px 0;
		background-color: #f9f9f9;
		border: 1px solid #ebeef5;
		border-radius: 4px;
	}
}

:deep(.el-descriptions__label) {
	width: 150px !important;
	font-size: 14px !important;
	font-weight: 600;
	white-space: nowrap;
	background: #e3e3e3 !important;
}
</style>
